import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from '@medifind/router';
import { Button, MFLink, Skeleton } from '@medifind/shared-basic-components';
import { getNullList, scrollToId } from '@medifind/utils';
import { useMediaQueryDown } from '@medifind/utils';
import { logEventGA4 } from '@medifind/zustand';
import RequestAppointmentModal from './request-appointment/RequestAppointmentModal';
import styles from './TopDataContainer.module.scss';

const Badge = ({ text, link }) => {
  return <div className={classNames(styles['badge-pill'], styles['accepted'])}>{link ?? text}</div>;
};

const PhoneNumberButton = ({ disabled, phone }) => {
  if (!phone) return null;
  return (
    <MFLink disabled={disabled} analyticsLinkType={'person-call'} to={`tel:${phone}`} noTabIndex>
      <Button label={`Call ${phone}`} color={'red'} type="juicy" disabled={disabled} showSkeleton={disabled} />
    </MFLink>
  );
};
const TopDataContainer = ({ hide, doctor, trialsResultsRecruiting }) => {
  const router = useRouter();
  const { query } = router;
  const { personId: queryPersonId, bookOnline } = query || {};
  const [displayModal, setDisplayModal] = useState(null);

  const {
    personId,
    name,
    displayName,
    credentials,
    specialties,
    demographics,
    phone,
    acceptsMedicare,
    acceptsNewPatients,
    appointmentAssistOptIn,
    telemedicine,
    appointmentURL,
    orgLogo,
    orgWebsite,
    isPromoted,
    address,
    addresses,
    yearsOfExperience,
  } = doctor || {};
  const { gradInstitution } = credentials || {};
  const gradYear = parseInt((gradInstitution && gradInstitution.gradYearNumber) || null, 10); // NaN if null/empty/bad
  const allAddresses = [address, ...(addresses ?? [])];

  const appointmentAssistAddresses = allAddresses.filter((addr) => {
    return addr?.appointmentAssist && addr?.isOrgAddress;
  });

  useEffect(() => {
    if (bookOnline) setDisplayModal(!!bookOnline);
  }, [queryPersonId]);

  const onModalClose = () => {
    setDisplayModal(false);
  };
  const onModalOpen = () => {
    setDisplayModal(true);
  };

  const badges = [
    acceptsNewPatients && <Badge key="newPatients" text="Accepting New Patients" />,
    telemedicine && <Badge key="telemedicine" text="Offers Telehealth" />,
    (yearsOfExperience ?? gradYear) && (
      <Badge key="background" text={`${yearsOfExperience ?? new Date().getFullYear() - gradYear} Years of Experience`} />
    ),
  ].filter(Boolean);

  const phoneNumber = phone ? phone : addresses?.find((address) => address.orgPhoneNumber !== null)?.orgPhoneNumber;

  return (
    <>
      <div className={classNames(styles['top-data-container'], hide && `hide--${hide}`)}>
        {/* orgLogo / "On Staff At:" */}
        {doctor && orgLogo ? (
          <>
            {/* <hr /> */}
            <div className={styles['org-logo-container']}>
              <div>On Staff At</div>
              {orgWebsite ? (
                <MFLink
                  aria-label={`person-org-link-${orgWebsite}`}
                  analyticsLinkType={'person-org-link'}
                  href={orgWebsite}
                  target="_blank"
                >
                  <img src={orgLogo + '-350x350.png'} alt="" />
                </MFLink>
              ) : (
                <img src={orgLogo + '-350x350.png'} alt="" />
              )}
            </div>
          </>
        ) : null}
        <div className={styles['top-data-container__row-wrapper']}>
          {/* recruiting trials */}
          {trialsResultsRecruiting?.length > 0 ? (
            <div className={styles['top-data']}>
              <b>Clinical Trials:</b>
              {
                <MFLink
                  className={classNames(styles['top-data__value'], styles['recruiting-trials'])}
                  href="#research"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToId('research');
                  }}
                >
                  Currently Recruiting for {trialsResultsRecruiting?.length} Trial{trialsResultsRecruiting?.length === 1 ? '' : 's'}
                </MFLink>
              }
            </div>
          ) : null}
          {/* acceptances */}
          {badges.length > 0 || !doctor ? (
            <div className={styles['top-data']}>
              {badges.length > 0 ? (
                <div className={classNames(styles['badges-container'], styles['accepteds'])}>{badges}</div>
              ) : (
                <Skeleton width={200} />
              )}
            </div>
          ) : null}
          <hr />
        </div>
      </div>

      <div className={classNames(styles['top-data__buttons'], hide && `hide--${hide}`)}>
        {/* Contact */}
        <div>
          {appointmentAssistOptIn && appointmentURL ? (
            /* Request appointment - External Link */
            <MFLink href={appointmentURL} analyticsLinkType="schedule-appointment" target="_blank">
              <Button color="red" label="Schedule Now" />
            </MFLink>
          ) : appointmentAssistOptIn && !appointmentURL ? (
            /* Request an Appointment Form */
            <RequestAppointmentModal
              classes={{ container: styles['button'] }}
              label={'Request appointment'}
              isPremium={false} // {isPremiumFlag} - Force false to not rollout the Long form. Need to revert while rolling out.
              doctorName={displayName || name}
              personId={personId}
              isOpen={displayModal}
              onClose={onModalClose}
              onOpen={onModalOpen}
              addresses={appointmentAssistAddresses?.length > 0 ? appointmentAssistAddresses : allAddresses}
              phone={phone}
              disabled={!doctor}
            />
          ) : phoneNumber && isPromoted ? (
            // Promoted Profile Phone number button
            <PhoneNumberButton classes={{ container: styles['button'] }} phone={phone} disabled={!doctor} />
          ) : phone ? (
            // Show if the request appt feature is off, or contact flag is set
            <PhoneNumberButton classes={{ container: styles['button'] }} phone={phone} disabled={!doctor} />
          ) : !doctor ? (
            <Skeleton width={180} />
          ) : null}{' '}
        </div>
      </div>
    </>
  );
};

export { TopDataContainer as default, TopDataContainer };
